import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import {
  CountrySelect,
  InputWithLabel,
  MultipleSelectWithChips,
  SEO,
} from 'main/components';
import { PRODUCT_OPTIONS_DEVELOPER, ProductOption } from 'main/consts';

export const Contact: React.FC = () => {
  const location = useLocation();
  const formRef = useRef<HTMLFormElement>(null);

  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );

  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  // Split Name State
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');

  const [email, setEmail] = useState<string>('');
  const [country, setCountry] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [yourRequest, setYourRequest] = useState<string>('');
  const [selectedProducts, setSelectedProducts] = useState<ProductOption[]>([]);

  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<boolean>(false);

  const isMobile = windowWidth <= 991;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    if (!firstName.trim()) newErrors.first_name = 'First name is required';
    if (!lastName.trim()) newErrors.last_name = 'Last name is required';

    if (!email.trim()) {
      newErrors.email = 'Email field is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Value is not a valid email format';
    }

    if (!country.trim()) newErrors.country_code = 'Country field is required';

    if (!turnstileToken) {
      newErrors.captcha = 'Please complete the security check';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setLoading(true);
      setSubmitError(false);

      try {
        if (formRef.current) {
          const formData = new FormData(formRef.current);

          const response = await fetch(
            'https://salesforce-form-proxy.orqa.workers.dev',
            {
              method: 'POST',
              body: formData,
            },
          );

          if (response.status === 200) {
            setIsSubmitted(true);

            // Clear form fields
            setFirstName('');
            setLastName('');
            setEmail('');
            setCountry('');
            setCompany('');
            setYourRequest('');
            setSelectedProducts([]);

            setTurnstileToken(null);
            if ((window as any).turnstile) {
              (window as any).turnstile.reset();
            }
          } else {
            setSubmitError(true);

            setTurnstileToken(null);
            if ((window as any).turnstile) {
              (window as any).turnstile.reset();
            }
          }
        }
      } catch (error) {
        setSubmitError(true);

        setTurnstileToken(null);
        if ((window as any).turnstile) {
          (window as any).turnstile.reset();
        }
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Define the callback function globally so Cloudflare can find it
    (window as any).onTurnstileSuccess = (token: string) => {
      setTurnstileToken(token);
      setErrors(prev => (prev ? { ...prev, captcha: '' } : null));
    };

    const scriptId = 'cf-turnstile-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }

    return () => {
      delete (window as any).onTurnstileSuccess;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const productParam = params.get('product');

    if (productParam) {
      const product = PRODUCT_OPTIONS_DEVELOPER.find(
        prod => prod.id === productParam,
      );
      if (product) {
        setSelectedProducts([product]);
      }
    }
  }, [location.search]);

  return (
    <>
      <SEO seoTitle="Contact Us - Product Information & Support - Orqa" />
      <section className="page soccer-contact-form">
        <div className="wrapper wrapper--narrow contact-form-wrapper">
          <div
            style={{
              width: '100%',
              textAlign: 'center',
              margin: '0px auto',
            }}
          >
            <h2
              style={{
                margin: '0 0 8px 0',
                color: '#FF2238',
                lineHeight: '1.25',
                paddingBottom: '8px',
              }}
              className="asda-contact-title"
            >
              Contact Us
            </h2>
            <p
              style={{ paddingBottom: '48px' }}
              className="asda-contact-description"
            >
              Have a question or want to learn more about our products? Fill out
              the form and we’ll get back to you with the information you need.
            </p>

            <form
              id="asda-form"
              ref={formRef}
              style={{
                display: 'flex',
                width: '100%',
                flexDirection: 'column',
                margin: '0 auto',
              }}
              onSubmit={handleSubmit}
            >
              {/* --- HIDDEN SALESFORCE FIELDS --- */}
              <input type="hidden" name="oid" value="00Dfn000006LhOH" />
              <input type="hidden" name="retURL" value="https://orqafpv.com" />
              <input type="hidden" name="lead_source" value="Website" />

              {/* --- MAIN COLUMNS WRAPPER --- */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '16px' : '24px',
                  width: '100%',
                  marginBottom: '16px',
                }}
              >
                <div
                  style={{
                    flex: 1,
                    width: isMobile ? '100%' : '0',
                    minWidth: '0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                  }}
                >
                  <InputWithLabel
                    required
                    setter={setFirstName}
                    setSelectedElement={setSelectedElement}
                    selectedElement={selectedElement}
                    name={'first_name'}
                    label={'First Name'}
                    errors={errors}
                    getter={firstName}
                  />

                  <InputWithLabel
                    required
                    setter={setLastName}
                    setSelectedElement={setSelectedElement}
                    selectedElement={selectedElement}
                    name={'last_name'}
                    label={'Last Name'}
                    errors={errors}
                    getter={lastName}
                  />

                  <InputWithLabel
                    required
                    setSelectedElement={setSelectedElement}
                    selectedElement={selectedElement}
                    name={'email'}
                    label={'Email'}
                    setter={setEmail}
                    errors={errors}
                    getter={email}
                  />

                  <CountrySelect
                    value={country}
                    onChange={setCountry}
                    error={errors?.country_code}
                    name="country_code"
                  />

                  <InputWithLabel
                    setSelectedElement={setSelectedElement}
                    selectedElement={selectedElement}
                    name={'company'}
                    label={'Organization'}
                    setter={setCompany}
                    getter={company}
                  />
                </div>

                {/* --- RIGHT COLUMN: PRODUCTS & TEXTAREA --- */}
                <div
                  style={{
                    flex: 1,
                    width: isMobile ? '100%' : '0',
                    minWidth: '0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                  }}
                >
                  {/* Products */}
                  <div>
                    <MultipleSelectWithChips
                      selectedOptions={selectedProducts}
                      setSelectedOptions={setSelectedProducts}
                      PRODUCT_OPTIONS={PRODUCT_OPTIONS_DEVELOPER}
                    />
                    {/* HIDDEN SELECT FOR PRODUCTS */}
                    <select
                      multiple
                      id="00Ncv0000073ii3"
                      name="00Ncv0000073ii3"
                      style={{ display: 'none' }}
                      value={selectedProducts.map(p => p.id)}
                      onChange={() => null}
                    >
                      {selectedProducts.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Textarea */}
                  <div
                    style={{
                      flex: 1,
                      position: 'relative',
                      minHeight: '150px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        height: '100%',
                      }}
                    >
                      <label
                        htmlFor="description-textarea"
                        className={
                          selectedElement === 'Your request' || yourRequest
                            ? `asda-label contact-form-label asda-label--active asda-label--outside`
                            : `asda-label contact-form-label asda-label--outside`
                        }
                        style={{
                          position: 'absolute',
                          transform:
                            yourRequest || selectedElement === 'Your request'
                              ? 'scale(0.85)'
                              : '',
                          top:
                            yourRequest || selectedElement === 'Your request'
                              ? '-12px'
                              : '12px',
                          left: '12px',
                          transition: 'all 0.2s ease-out',
                          backgroundColor:
                            yourRequest || selectedElement === 'Your request'
                              ? 'black'
                              : 'transparent',
                          padding:
                            yourRequest || selectedElement === 'Your request'
                              ? '0 4px'
                              : '0',
                          zIndex: 1,
                          pointerEvents: 'none',
                          fontSize: '16px',
                          color:
                            selectedElement === 'Your request' || yourRequest
                              ? 'white'
                              : '#999',
                        }}
                      >
                        Your request
                      </label>
                      <textarea
                        name="description"
                        id="description-textarea"
                        value={yourRequest}
                        onChange={e => setYourRequest(e.target.value)}
                        className={'asda-input asda-textarea'}
                        style={{
                          height: '100%',
                          width: '100%',
                          resize: 'none',
                          paddingTop: '16px',
                          minHeight: '150px',
                          marginBottom: '12px',
                        }}
                        onFocus={() => setSelectedElement('Your request')}
                        onBlur={() => setSelectedElement(null)}
                      />
                      {errors?.yourrequest && (
                        <p className="asda__error__message">
                          {errors.yourrequest}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* --- SUBMIT SECTION (Message Left, Button Right) --- */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  marginTop: '10px',
                  width: '100%',
                  gap: isMobile && (isSubmitted || submitError) ? '15px' : '0',
                }}
              >
                {/* STATUS MESSAGE AREA: Success OR Error */}
                {(isSubmitted || submitError) && (
                  <div
                    style={{
                      textAlign: 'left',
                      width: isMobile ? '100%' : 'auto',
                      marginBottom: isMobile ? '16px' : '',
                    }}
                  >
                    {isSubmitted && !submitError && (
                      <p
                        style={{
                          fontSize: '14px',
                          color: '#07bc0c',
                          margin: 0,
                        }}
                      >
                        Form successfully submitted!
                      </p>
                    )}
                    {submitError && (
                      <p
                        style={{
                          fontSize: '14px',
                          color: '#FF2238',
                          margin: 0,
                        }}
                      >
                        Unexpected error occurred
                      </p>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="asda__submit-button"
                  style={{
                    marginLeft: isMobile ? '0' : 'auto',
                    flexShrink: 0,
                  }}
                >
                  {loading ? (
                    <div className="asda__button-spinner--wrapper">
                      <div className="asda__button-spinner" />
                    </div>
                  ) : (
                    <p>Submit</p>
                  )}
                </button>
              </div>

              {/* --- TURNSTILE WIDGET --- */}
              <div
                style={{
                  marginTop: '26px',
                  width: '100%',
                  maxInlineSize: isMobile ? 'fit-content' : '',
                }}
              >
                <div
                  style={{
                    marginBottom: '12px',
                    display: 'flex',
                    justifyContent: isMobile ? 'center' : 'flex-end',
                    width: '100%',
                  }}
                >
                  <div
                    className="cf-turnstile"
                    data-sitekey={process.env.REACT_APP_DATA_SITE_KEY}
                    data-callback="onTurnstileSuccess"
                    data-theme="dark"
                    data-appearance="interaction-only"
                  />
                </div>

                {errors?.captcha && (
                  <p
                    className="asda__error__message"
                    style={{
                      textAlign: isMobile ? 'center' : 'right',
                      marginTop: '-8px',
                      marginBottom: '12px',
                    }}
                  >
                    {errors.captcha}
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
};
